import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import React, { Component, forwardRef } from 'react';
import { makeStyles } from 'tss-react/mui';

import FilterSelect, { type Item } from '~/components/filter-select';
import { GenericDetailedError } from '~/errors/GenericDetailedError';
import { DimensionsService, type TimeDimension } from '~/services/dimensions';
import { ExportService, type ExportType, type Version } from '~/services/exports';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';

const useStyles = makeStyles()({
  exportSelect: {
    width: '100%',
  },
  timeDimensionSelect: {
    width: '100%',
  },
  versionSelect: {
    width: '100%',
  },
  dimensionSelect: {
    width: '100%',
  },
  textField: {
    height: 'auto',
    marginTop: '4px',
  },
  xmlDialog: {
    minHeight: '70%',
    maxHeight: '70%',
    maxWidth: '70%',
    minWidth: '70%',
  },
  form: {
    height: '100%',
  },
});

interface NewExportDialogProps {
  onDone: () => void;
  onError: (error: GenericDetailedError) => void;
  classes: ReturnType<typeof useStyles>['classes'];
}

interface TextFiltering {
  id: string;
  value: string;
}

interface NewExportDialogState {
  open: boolean;
  selectedTimeDimension: string;
  timeDimensions: TimeDimension[];
  selectedVersion: string;
  versions: Version[];
  dimensionTypeSelect: JSX.Element[];
  filterKey: TextFiltering[];
  filterText: TextFiltering[];
  legacyMode: boolean;
  exportTypes: ExportType[];
  selectedExportTypeId: number;
}

interface BuildSelectArgs {
  error?: boolean;
  options: () => {label: string, value: string}[];
  label: string;
  name: string;
  onChange: (item: Item) => void;
  shrinkLabel: boolean;
  key: number;
}

export class NewExportDialogRaw extends Component<NewExportDialogProps, NewExportDialogState> {
  constructor(props: NewExportDialogProps) {
    super(props);
    this.state = {
      open: false,
      selectedTimeDimension: '',
      timeDimensions: [],
      selectedVersion: '',
      versions: [],
      dimensionTypeSelect: [],
      filterKey: [],
      filterText: [],
      legacyMode: false,
      exportTypes: [
        { id: 30, name: 'xml' }, // Hardcoded id for XML export-/submission type
        { id: 32, name: 'excel' },
      ], // Hardcoded id for Excel export-/submission type
      selectedExportTypeId: 30,
    };
  }

  openDialog = async () => {
    this.setState({
      open: true,
    });
    const timeDimensions = await DimensionsService.getSubmissionYears();
    this.setState({
      timeDimensions,
      selectedTimeDimension: timeDimensions[0].name,
    });
  };

  closeDialog = () => {
    this.setState({
      open: false,
      filterKey: [],
      filterText: [],
      dimensionTypeSelect: [],
    });
  };

  adjustKey = (key: string) => {
    let adjustedKey = key.toLocaleLowerCase();
    if (adjustedKey === 'fileidentity') {
      adjustedKey = 'fileIdentity';
    } else if (adjustedKey === 'undergrupp') {
      adjustedKey = 'subGroup';
    } else if (adjustedKey === 'crfcode') {
      adjustedKey = 'crfCode';
    } else if (adjustedKey === 'nfrcode') {
      adjustedKey = 'nfrCode';
    } else if (adjustedKey === 'year') {
      adjustedKey = 'pivotLabel';
    } else if (adjustedKey === 'datatype') {
      adjustedKey = 'dataType';
    }
    return adjustedKey;
  };

  createExport = async () => {
    try {
      const filter: { [x: string]: string[] } = {};
      this.state.filterKey.forEach((key) => {
        const searchTerm = this.adjustKey(key.value);
        this.state.filterText.forEach((text) => {
          if (key.id === text.id) {
            if (filter[searchTerm] === undefined) {
              filter[searchTerm] = [text.value];
            } else {
              filter[searchTerm].push(text.value);
            }
          }
        });
      });
      await ExportService.initiateExport(
        this.state.selectedTimeDimension,
        this.state.selectedVersion,
        this.state.selectedExportTypeId,
        filter,
        this.state.legacyMode
      );
      this.props.onDone();
    } catch (error) {
      if (error instanceof GenericDetailedError) {
        this.props.onError(error);
      } else {
        console.error(error);
      }
    }

    this.closeDialog();
  };
  createDimensionTypeKey = () => {
    let uniqKey = 0;
    if (this.state.dimensionTypeSelect.length > 0) {
      uniqKey =
        Math.max.apply(
          Math,
          this.state
              .dimensionTypeSelect
              .map((elem) => Number.parseInt(elem.key ?? '0', 10))
        ) + 1;
    }
    return uniqKey;
  };

  getDimensionTypes = (uKey: number) => {
    const dimensionTypes = [
      'Category',
      'Classification',
      'Measure',
      'Gas',
      'CRFCode',
      'NFRCode',
      'Undergrupp',
      'Source',
      'Method',
      'Target',
      'Option',
      'Type',
      'Year',
      'Datatype',
      'UID',
    ];
    return dimensionTypes.map((t: string) => ({
      label: t,
      value: uKey.toString(),
    }));
  };

  createFilter = () => {
    const uKey = this.createDimensionTypeKey();
    const opt = () => this.getDimensionTypes(uKey);
    const newItem = [
      ...this.state.dimensionTypeSelect,
      this.buildFilterSelect({
        options: opt,
        error: false,
        label: 'Typ',
        name: 'dimensionType',
        onChange: this.handleDimensionTypeChange,
        shrinkLabel: true,
        key: uKey,
      }),
    ];
    this.setState({ dimensionTypeSelect: newItem });
  };

  handleTimeDimensionChange = (event: SelectChangeEvent<string>) => {
    this.setState({ selectedTimeDimension: event.target.value });
  };

  private handleDimensionTypeChange = (i: Item | null) => {
    if (i != null) {
      const dimension = i;
      if (this.state.filterKey.find((val) => val.id === dimension.value)) {
        this.setState({
          filterKey: this.state.filterKey.map((ent) => {
            if (ent.id === dimension.value) {
              return { id: dimension.value, value: dimension.label };
            } else {
              return ent;
            }
          }),
        });
      } else {
        this.setState({
          filterKey: [...this.state.filterKey, ...[{ id: dimension.value, value: dimension.label }]],
        });
      }
    }
  };

  handleExportTypeChange = (event: SelectChangeEvent<number>) => {
    this.setState({ selectedExportTypeId: Number(event.target.value) });
  };

  handleVersionChange = (event: SelectChangeEvent<string>) => {
    this.setState({ selectedVersion: event.target.value });
  };

  handelFilterTextUpdate = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (this.state.filterText.find((val) => val.id === event.target.id)) {
      this.setState({
        filterText: this.state.filterText.map((ent) => {
          if (ent.id === event.target.id) {
            return { id: event.target.id, value: event.target.value };
          } else {
            return ent;
          }
        }),
      });
    } else {
      this.setState({
        filterText: [...this.state.filterText, ...[{ id: event.target.id, value: event.target.value }]],
      });
    }
  };

  componentDidUpdate = async (prevProps: NewExportDialogProps, prevState: NewExportDialogState) => {
    if (prevState.selectedTimeDimension !== this.state.selectedTimeDimension) {
      const versions = await ExportService.getTimeDimensionVersions(this.state.selectedTimeDimension);
      this.setState({
        versions,
        selectedVersion: versions.length > 0 ? versions[0].name : '',
      });
    }
  };

  removeFilter = (key: string) => {
    const tmpDimArr = [...this.state.dimensionTypeSelect];
    let idx = tmpDimArr.findIndex((elem) => elem.key === key);
    tmpDimArr.splice(idx, 1);
    const tmpFKeyArr = [...this.state.filterKey];
    idx = tmpFKeyArr.findIndex((elem) => elem.id === key);
    tmpFKeyArr.splice(idx, 1);
    const tmpFTextArr = [...this.state.filterText];
    idx = tmpFTextArr.findIndex((elem) => elem.id === key);
    tmpFTextArr.splice(idx, 1);
    this.setState({
      dimensionTypeSelect: [...tmpDimArr],
      filterKey: [...tmpFKeyArr],
      filterText: [...tmpFTextArr],
    });
  };

  private buildFilterSelect = (args: BuildSelectArgs) => {
    const props = {
      error: args.error,
      defaultOptions: true,
      loadOptions: args.options,
      cacheOptions: false,
      placeholder: '',
      label: args.label,
      name: args.name,
      onChange: args.onChange,
      shrinkLabel: args.shrinkLabel,
    };
    return (
      <React.Fragment key={args.key}>
        <Grid item xs={12} md={7}>
          {' '}
          <FilterSelect {...props} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            variant="standard"
            fullWidth={true}
            required={true}
            label="Value"
            id={args.key.toString()}
            onChange={this.handelFilterTextUpdate}
          />
        </Grid>
        <Grid item xs={12} md={1}>
          <IconButton onClick={() => this.removeFilter(args.key.toString())} size="large">
            <CloseIcon />
          </IconButton>
        </Grid>
      </React.Fragment>
    );
  };

  render() {
    const { classes } = this.props;

    return (
      <Dialog
        open={this.state.open}
        onClose={this.closeDialog}
        aria-labelledby="form-dialog-title"
        classes={{ paper: classes.xmlDialog }}
      >
        <DialogTitle id="form-dialog-title">Skapa export</DialogTitle>
        <DialogContent className={classes.form}>
          <form data-testid="dialog-form">
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <FormControl variant="standard" className={classes.exportSelect}>
                  <InputLabel>Export type</InputLabel>
                  <Select
                    variant="standard"
                    onChange={this.handleExportTypeChange}
                    value={this.state.selectedExportTypeId}
                  >
                    {this.state.exportTypes.map((value) => {
                      return (
                        <MenuItem value={value.id} key={value.id}>
                          {value.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item alignContent={'end'} xs={12} md={3}>
                <FormGroup>
                  <FormControlLabel control={<Checkbox checked={this.state.legacyMode} onChange={() => this.setState({legacyMode: !this.state.legacyMode})} />} label="Använd gamla export formatet" />
                </FormGroup>
              </Grid>

              <Grid item xs={12}>
                <DialogContentText>
                  Välj vilket submissionsår och vilken version du vill skapa export för.
                </DialogContentText>
              </Grid>
              <Grid item xs={12} md={8}>
                <FormControl variant="standard" className={classes.timeDimensionSelect}>
                  <InputLabel htmlFor="submission-year-picker">Submissionsår</InputLabel>
                  <Select
                    variant="standard"
                    onChange={this.handleTimeDimensionChange}
                    value={this.state.selectedTimeDimension}
                    data-testid={'submission-year-picker'}
                    inputProps={{
                      name: 'year',
                      id: 'submission-year-picker',
                    }}
                  >
                    {this.state.timeDimensions.map((td) => {
                      return (
                        <MenuItem value={td.name} key={td.id} data-testid={`MenuItem-${td.id}`}>
                          {td.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl variant="standard" className={classes.versionSelect}>
                  <InputLabel htmlFor="submission-year-picker">Version</InputLabel>
                  <Select
                    variant="standard"
                    onChange={this.handleVersionChange}
                    value={this.state.selectedVersion}
                    inputProps={{
                      name: 'version',
                      id: 'submission-year-version-picker',
                    }}
                  >
                    <MenuItem value="" disabled={true}>
                      Ingen version
                    </MenuItem>
                    ;
                    {this.state.versions.map((v) => {
                      return (
                        <MenuItem value={v.name} key={v.id}>
                          {v.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              {this.state.dimensionTypeSelect.map((elem) => {
                return elem;
              })}
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.createFilter} disabled={!this.state.selectedVersion} color="primary">
            lägg till Filter
          </Button>
          <Button onClick={this.closeDialog} color="primary">
            Avbryt
          </Button>
          <Button onClick={this.createExport} disabled={!this.state.selectedVersion} color="primary">
            Exportera
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export const NewExportDialog = forwardRef<NewExportDialogRaw, Omit<NewExportDialogProps, 'classes'>>((props, ref) => {
  const { classes } = useStyles();

  return <NewExportDialogRaw ref={ref} classes={classes} {...props} />;
});

export default NewExportDialog;
